import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import spinozaService from '../services/spinozaService';
import { useStackFlowOptional } from './StackFlowContext';

const SpinozaContext = createContext(null);

// Proactive hint messages per pillar (when seed data is present)
const SEED_HINTS = {
  exchange_rates: (seed) =>
    `I've pre-filled ${seed.sourceCcy || 'your source currency'}/${seed.sinkCcy || 'sink currency'} and ${seed.amount ? seed.amount.toLocaleString() : 'your amount'} from your yield discovery. Click Demo to get competing bank quotes.`,
  hedging: (seed) =>
    `I've set your capital currency to ${seed.capitalCcy || 'USD'} and amount to ${seed.amount ? seed.amount.toLocaleString() : 'your amount'} based on your earlier selections.`,
  compliance: () =>
    `Good progress! Your yield discovery details have been noted. Click Demo to check pre-approval status across partner banks.`,
  liquidity: () =>
    `You're making great progress through the stack. View your portfolio and secondary market here.`,
};

export const useSpinoza = () => {
  const ctx = useContext(SpinozaContext);
  if (!ctx) throw new Error('useSpinoza must be used within SpinozaProvider');
  return ctx;
};

/**
 * SpinozaProvider — manages Spinoza chat state.
 * Messages[], state (idle/listening/thinking/speaking), open toggle.
 * Mounted in App.js inside AuthProvider.
 */
export const SpinozaProvider = ({ children, selectStack, navigate }) => {
  const [messages, setMessages] = useState([]);
  const [state, setState] = useState('idle'); // idle | listening | thinking | speaking
  const [isTyping, setIsTyping] = useState(false); // true while SpinozaBubble typewriter is active
  const [isOpen, setIsOpen] = useState(false);
  const hintedPillarsRef = useRef(new Set());

  // Track pathname for route-aware context (provider sits outside Router)
  const [pathname, setPathname] = useState(() =>
    typeof window !== 'undefined' ? window.location.pathname : '/'
  );
  const prevPathnameRef = useRef(pathname);

  useEffect(() => {
    const update = () => setPathname(window.location.pathname);

    // Catch back/forward navigation
    window.addEventListener('popstate', update);

    // Catch React Router pushState/replaceState calls
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    window.history.pushState = function (...args) {
      origPush.apply(this, args);
      update();
    };
    window.history.replaceState = function (...args) {
      origReplace.apply(this, args);
      update();
    };

    return () => {
      window.removeEventListener('popstate', update);
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
    };
  }, []);

  // Stack flow integration for proactive hints
  const stackFlow = useStackFlowOptional();

  // Proactive hint when user navigates to a panel with seed data
  useEffect(() => {
    if (!stackFlow) return;
    const { currentPillar, hasSeedData, getSeedData } = stackFlow;
    if (!currentPillar || !hasSeedData(currentPillar)) return;
    if (hintedPillarsRef.current.has(currentPillar)) return;

    const hintFn = SEED_HINTS[currentPillar];
    if (!hintFn) return;

    hintedPillarsRef.current.add(currentPillar);
    const seed = getSeedData(currentPillar);
    const hintText = hintFn(seed || {});

    setMessages(prev => [...prev, {
      role: 'assistant',
      text: hintText,
      intent: 'flow.hint',
      timestamp: Date.now(),
    }]);

    // Open Spinoza overlay if collapsed
    setIsOpen(true);
  }, [stackFlow?.currentPillar]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset conversation when route changes so Spinoza re-greets with new page context
  useEffect(() => {
    if (prevPathnameRef.current !== pathname) {
      prevPathnameRef.current = pathname;
      setMessages([]);
      setState('idle');
      setIsTyping(false);
    }
  }, [pathname]);

  // Persist collapsed state
  useEffect(() => {
    try {
      const stored = localStorage.getItem('ff_spinoza_collapsed');
      if (stored === 'true') setIsOpen(false);
    } catch {}
  }, []);

  // Send contextual greeting when open with no messages (first open or after route change)
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const greeting = spinozaService.getGreeting(pathname);
      setMessages([{
        role: 'assistant',
        text: greeting,
        timestamp: Date.now(),
      }]);
    }
  }, [isOpen, messages.length, pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleOpen = useCallback(() => {
    setIsOpen(prev => {
      const next = !prev;
      try { localStorage.setItem('ff_spinoza_collapsed', String(!next)); } catch {}
      if (!next) {
        setState('idle');
        setIsTyping(false);
      }
      return next;
    });
  }, []);

  const ask = useCallback(async (text) => {
    // Add user message
    setMessages(prev => [...prev, { role: 'user', text, timestamp: Date.now() }]);
    setState('thinking');

    const ctx = { selectStack, navigate };
    if (stackFlow) {
      ctx.getCompletedPillars = stackFlow.getCompletedPillars;
      ctx.getNextPillar = stackFlow.getNextPillar;
    }

    try {
      const response = await spinozaService.process(text, ctx);

      setMessages(prev => [...prev, {
        role: 'assistant',
        text: response.text,
        intent: response.intent,
        timestamp: Date.now(),
      }]);

      setState('idle');
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: 'I encountered an issue. Please try again.',
        timestamp: Date.now(),
      }]);
      setState('idle');
    }
  }, [selectStack, navigate, stackFlow]);

  const run = useCallback(async (intentName, params) => {
    setState('thinking');
    // Direct intent execution — for programmatic use
    const intent = require('../lib/spinoza/intents').INTENTS.find(i => i.name === intentName);
    if (intent) {
      const ctx = { selectStack, navigate };
      if (stackFlow) {
        ctx.getCompletedPillars = stackFlow.getCompletedPillars;
        ctx.getNextPillar = stackFlow.getNextPillar;
      }
      const result = intent.run(ctx, params || {});
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: result,
        intent: intentName,
        timestamp: Date.now(),
      }]);
    }
    setState('idle');
  }, [selectStack, navigate, stackFlow]);

  const value = {
    messages,
    state,
    isTyping,
    setIsTyping,
    ask,
    run,
    isOpen,
    toggleOpen,
  };

  return (
    <SpinozaContext.Provider value={value}>
      {children}
    </SpinozaContext.Provider>
  );
};

export default SpinozaContext;
